import { useEffect, useState } from "react";
import { captureError } from "@/lib/sentry";
import { useParams, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { GAMLY_PERSONALITIES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import AskGamlyOverlay from "@/components/AskGamlyOverlay";
import PlayByPlay from "@/components/PlayByPlay";
import AIModesPanel from "@/components/AIModesPanel";
import MLBGameday from "@/components/MLBGameday";
import NBAGameday from "@/components/NBAGameday";
import TeamLogo from "@/components/TeamLogo";
import { ArrowLeftIcon, TelevisionIcon } from "@phosphor-icons/react";
import WorldSeriesBadge from "@/components/WorldSeriesBadge";

export default function LiveGame() {
  const { user } = useAuth();
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [language] = useState(user?.language_preference || "en");
  const [askSignal, setAskSignal] = useState(0);

  const persona = GAMLY_PERSONALITIES.find((p) => p.value === (user?.personality || "broadcaster"));

  const [status, setStatus] = useState("loading"); // loading | ready | not_found | error

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/games/${id}`)
      .then((r) => {
        setGame(r.data);
        setStatus("ready");
      })
      .catch((err) => {
        setGame(null);
        setStatus(err?.response?.status === 404 ? "not_found" : "error");
        if (err?.response?.status !== 404) {
          captureError("game load failed:", err);
        }
      });
  }, [id]);

  if (status === "loading") {
    return (
      <AppLayout>
        <div className="text-white/60" data-testid="live-loading">Loading game…</div>
      </AppLayout>
    );
  }

  if (status === "not_found") {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="live-not-found">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF453A] mb-2">404 · Game Not Found</p>
          <h1 className="font-display font-bold text-4xl uppercase tracking-tighter mb-3">This game isn't on today's slate</h1>
          <p className="text-sm text-white/60 mb-6">
            Gamly's slate refreshes daily — old bookmarks won't carry over. Pop back to today's games.
          </p>
          <Link
            to="/games"
            data-testid="live-not-found-cta"
            className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider"
          >
            <ArrowLeftIcon /> See today's games
          </Link>
        </div>
      </AppLayout>
    );
  }

  if (status === "error" || !game) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="live-error">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF453A] mb-2">Connection issue</p>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tighter mb-3">Couldn't load this game</h1>
          <p className="text-sm text-white/60 mb-6">Check your connection and try again.</p>
          <Link
            to="/games"
            className="inline-flex items-center gap-1 px-5 py-2 rounded-md border border-white/20 text-white/80 text-xs font-bold uppercase tracking-wider hover:bg-white/5"
          >
            <ArrowLeftIcon /> Back to slate
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {game.league === "MLB" ? (
        <MLBGameday game={game} gameId={id} language={language} onAskGamly={() => setAskSignal((s) => s + 1)} />
      ) : game.league === "NBA" ? (
        <NBAGameday game={game} gameId={id} language={language} onAskGamly={() => setAskSignal((s) => s + 1)} />
      ) : (
      <>
      <Link to="/games" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white mb-4" data-testid="back-to-games">
        <ArrowLeftIcon size={14} /> Back to slate
      </Link>
      {/* Scoreboard */}
      <div className="bg-[#121212] border border-white/10 rounded-md overflow-hidden mb-6" data-testid="scoreboard">
        <div className="px-6 py-3 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-white/40">{game.league}</span>
            <WorldSeriesBadge game={game} />
            {game.tv && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/40">
                <TelevisionIcon size={12} /> {game.tv}
              </span>
            )}
          </div>
          {game.status === "LIVE" ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#FF0000]/15 border border-[#FF453A]/40">
              <span className="live-dot" />
              <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[#FF453A]">{game.period_label}</span>
            </span>
          ) : (
            <span className="text-[11px] font-black uppercase tracking-[0.15em] text-white/60">{game.period_label}</span>
          )}
        </div>
        <div className="px-6 py-6 grid grid-cols-2 gap-4">
          <ScoreSide abbr={game.away_team_abbr} name={game.away_team} score={game.away_score} color={game.away_team_color} logo={game.away_team_logo} />
          <ScoreSide abbr={game.home_team_abbr} name={game.home_team} score={game.home_score} color={game.home_team_color} logo={game.home_team_logo} home />
        </div>
        {game.status !== "UPCOMING" && (
          <div className="px-6 pb-5">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1">
              <span>Win probability</span>
              <span>
                {game.away_team_abbr} {game.win_probability_away}% · {game.home_team_abbr} {game.win_probability_home}%
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden bg-white/5 flex">
              <div className="h-full" style={{ width: `${game.win_probability_away}%`, backgroundColor: game.away_team_color }} />
              <div className="h-full" style={{ width: `${game.win_probability_home}%`, backgroundColor: game.home_team_color }} />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="font-display font-bold text-2xl uppercase tracking-tighter mb-3">Key Matchup</h2>
          <div className="p-5 rounded-md bg-[#121212] border border-white/10 mb-6">
            <p className="font-display font-bold text-xl uppercase">{game.key_matchup}</p>
            <p className="text-white/70 mt-2 text-sm">{game.storyline}</p>
            {game.ai_summary && <p className="text-white/50 mt-2 text-xs">{game.ai_summary}</p>}
          </div>

          <AIModesPanel gameId={id} defaultLanguage={language} />
        </div>

        <aside>
          <h2 className="font-display font-bold text-2xl uppercase tracking-tighter mb-3">Quick Facts</h2>
          <div className="p-5 rounded-md bg-[#121212] border border-white/10 space-y-2 text-sm">
            <Row label="League" value={game.league} />
            <Row label="Date" value={game.date} />
            <Row label="TV" value={game.tv || "—"} />
            <Row label="Status" value={game.status} />
          </div>

          <div className="mt-4">
            <PlayByPlay gameId={id} gameStatus={game.status} />
          </div>
        </aside>
      </div>
      </>
      )}

      <AskGamlyOverlay gameId={id} language={language} persona={persona} openSignal={askSignal} />
    </AppLayout>
  );
}

function ScoreSide({ abbr, name, score, color, logo, home }) {
  return (
    <div className={`flex flex-col gap-2 ${home ? "items-end text-right" : "items-start"}`}>
      <div className={`flex items-center gap-3 ${home ? "flex-row-reverse" : ""}`}>
        <TeamLogo abbr={abbr} logo={logo} color={color} size={48} />
        <div>
          <p className="font-display font-bold uppercase text-base tracking-tight text-white/90 max-w-[180px] truncate">{name}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">{home ? "Home" : "Away"}</p>
        </div>
      </div>
      <div className="font-num text-6xl md:text-7xl font-black">{score}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">{label}</span>
      <span className="text-white/85 font-medium">{value}</span>
    </div>
  );
}
